/**
 * @module builder
 */
import type { TieredPackageFeature, TieredPackageTier, TieredPackageTierId } from 'paintscout'
import { uuid } from '../../util'
import { TIER_ORDER } from './default-tiered-packages'

/**
 * Feature lists are tier specific: each tier has its own list, and a row is on a tier's list only
 * while `included` holds a key for that tier. The rows themselves are held once for the package set
 * so a label typed on one tier reads the same on the others, and so the three lists line up as a
 * Good/Better/Best comparison. Older package sets stored a separate `string[]` on every tier, so
 * everything reads through getTieredPackageFeatures, which folds those legacy lists into rows.
 *
 * Per tier, `included[tierId]` is `true` for a checkmark, `false` for a greyed X ("Not included"),
 * and missing when the row is not on that tier's list at all.
 */

export type TieredPackageFeatureSource = {
  features?: TieredPackageFeature[]
  tiers: Record<TieredPackageTierId, TieredPackageTier>
}

const allIncluded = (): TieredPackageFeature['included'] =>
  TIER_ORDER.reduce((acc, tierId) => ({ ...acc, [tierId]: true }), {} as TieredPackageFeature['included'])

/**
 * The tiers a row is on, given the tier it was added from: that tier and every tier above it.
 *
 * Packages are cumulative in the customer's reading — Premium is Standard plus more — so a row
 * added on Standard belongs to Premium too, and a user who wants it on Standard alone takes it off
 * Premium by hand. This also covers a row saved with an `origin` but no `included` map, written
 * before the map existed; those rows were cumulative in the same way.
 */
export function getFeatureInclusionFromOrigin(origin?: TieredPackageTierId): TieredPackageFeature['included'] {
  if (!origin) {
    return allIncluded()
  }
  const originIndex = TIER_ORDER.indexOf(origin)
  if (originIndex < 0) {
    return allIncluded()
  }
  return TIER_ORDER.slice(originIndex).reduce(
    (acc, tierId) => ({ ...acc, [tierId]: true }),
    {} as TieredPackageFeature['included']
  )
}

/**
 * A blank row on one tier's list — what "Add Feature" creates.
 *
 * The row lands on the tier it was added from and on every tier above it: a row typed on Basic
 * shows on all three, one typed on Premium on Premium alone. The user then takes it off any tier
 * that should not carry it. Called with no origin it lands on all three, which is what a row
 * created outside the tier tabs (the seed row of a brand new template) means.
 */
export function createTieredPackageFeature(label = '', origin?: TieredPackageTierId): TieredPackageFeature {
  return { id: uuid(), label, origin, included: getFeatureInclusionFromOrigin(origin) }
}

/**
 * Fold the legacy per-tier `features: string[]` arrays into rows. Rows are keyed by label so a
 * feature repeated across tiers becomes one row on each of those tiers' lists, which is how these
 * lists were authored in practice. A tier that never listed the feature does not get the row — the
 * legacy format had no way to say "not included", only to leave the feature out.
 */
function migrateLegacyFeatures(tiers: Record<TieredPackageTierId, TieredPackageTier>): TieredPackageFeature[] {
  const rows: TieredPackageFeature[] = []
  const byLabel = new Map<string, TieredPackageFeature>()

  TIER_ORDER.forEach((tierId) => {
    ;(tiers[tierId]?.features ?? []).forEach((label) => {
      const key = label.trim().toLowerCase()
      const existing = key ? byLabel.get(key) : undefined
      if (existing) {
        existing.included[tierId] = true
        return
      }
      const row: TieredPackageFeature = { id: uuid(), label, included: { [tierId]: true } }
      rows.push(row)
      if (key) {
        byLabel.set(key, row)
      }
    })
  })

  return rows
}

/**
 * Every feature row in a package set, across all tiers, migrating legacy per-tier lists when needed.
 * Filter with getTierFeatures for the list a single tier shows.
 *
 * Rows saved without an `included` map fall back to their `origin` cascade, and to "on every list"
 * when they carry neither — so a row written by an older client still renders.
 */
export function getTieredPackageFeatures(source: TieredPackageFeatureSource): TieredPackageFeature[] {
  if (source.features) {
    // Returned as-is when every row already has its map, so callers keep referential identity.
    if (source.features.every((feature) => !!feature?.included)) {
      return source.features
    }
    return source.features.map((feature) =>
      feature?.included ? feature : { ...feature, included: getFeatureInclusionFromOrigin(feature?.origin) }
    )
  }
  return migrateLegacyFeatures(source.tiers)
}

/**
 * One tier's list — the rows on it, each with whether that tier shows a checkmark or an X.
 * Rows that are not on this tier's list are left out entirely.
 */
export function getTierFeatures(
  source: TieredPackageFeatureSource,
  tierId: TieredPackageTierId
): Array<{ id: string; label: string; included: boolean }> {
  return getTieredPackageFeatures(source)
    .filter((feature) => feature.included?.[tierId] !== undefined)
    .map((feature) => ({
      id: feature.id,
      label: feature.label,
      included: feature.included[tierId] === true
    }))
}

/** Just the labels a tier includes — the shape the customer-facing card renders. */
export function getIncludedFeatureLabels(source: TieredPackageFeatureSource, tierId: TieredPackageTierId): string[] {
  return getTierFeatures(source, tierId)
    .filter((feature) => feature.included && feature.label.trim())
    .map((feature) => feature.label)
}

/**
 * Package descriptions are plain text, but the quote-level setup wizard used to author them
 * with the rich-text editor, so saved values can still be TipTap HTML. Rendering those as text
 * would show raw markup to the customer, and rendering them as HTML is an injection sink — so
 * flatten to text: block ends become newlines, tags go, entities decode.
 */
export function getTieredPackageDescriptionText(description?: string): string {
  if (!description) {
    return ''
  }
  if (!/<[a-z/][\s\S]*>/i.test(description)) {
    return description
  }
  return description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Remove a feature from one tier's list.
 *
 * Only that tier's key goes, so the other tiers keep the row and their own checkmark or X. The row
 * itself is dropped once no tier is left carrying it, since a row on no list has nothing to show.
 */
export function removeTieredPackageFeatureFromTier(
  features: TieredPackageFeature[],
  id: string,
  tierId: TieredPackageTierId
): TieredPackageFeature[] {
  const feature = features.find((f) => f.id === id)
  if (!feature) {
    return features
  }
  const included = { ...(feature.included ?? getFeatureInclusionFromOrigin(feature.origin)) }
  delete included[tierId]
  if (!TIER_ORDER.some((otherId) => included[otherId] !== undefined)) {
    return features.filter((f) => f.id !== id)
  }
  return features.map((f) => (f.id === id ? { ...f, included } : f))
}

/** Mark a row included (checkmark) or not included (greyed X) on one tier, leaving the rest alone. */
export function setTieredPackageFeatureIncluded(
  features: TieredPackageFeature[],
  id: string,
  tierId: TieredPackageTierId,
  included: boolean
): TieredPackageFeature[] {
  return features.map((feature) =>
    feature.id === id
      ? {
          ...feature,
          included: { ...(feature.included ?? getFeatureInclusionFromOrigin(feature.origin)), [tierId]: included }
        }
      : feature
  )
}

/** Move a row to sit where another row currently is (drag reorder). */
export function reorderTieredPackageFeatures(
  features: TieredPackageFeature[],
  draggedId: string,
  targetId: string
): TieredPackageFeature[] {
  if (draggedId === targetId) {
    return features
  }
  const next = [...features]
  const from = next.findIndex((f) => f.id === draggedId)
  const to = next.findIndex((f) => f.id === targetId)
  if (from < 0 || to < 0) {
    return features
  }
  const [row] = next.splice(from, 1)
  next.splice(to, 0, row)
  return next
}
